import { Component } from '@theme/component';

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const DURATION_MS = 1600;

/** Het eerste getal in de tekst, Nederlands geschreven: "28.000", "1.250,5", "40". */
const NUMBER = /\d{1,3}(?:[.  ]\d{3})+(?:,\d+)?|\d+(?:,\d+)?/;

/**
 * @typedef {object} ParsedNumber
 * @property {string} before - Tekst vóór het getal
 * @property {string} after - Tekst na het getal ("+", " %")
 * @property {number} value
 * @property {number} decimals - Aantal cijfers achter de komma
 * @property {string} separator - Duizendtalscheiding zoals in de tekst, of leeg
 */

/**
 * Telt een kerncijfer op van 0 tot de waarde zodra het in beeld komt
 * ("28.000+" in "Samen goed bezig").
 *
 * De server zet de echte tekst al in de pagina: zonder JavaScript, in de
 * theme editor, bij `prefers-reduced-motion` en bij een tekst zonder getal
 * ("[X]") blijft die gewoon staan. Alleen het eerste getal telt; wat ervoor
 * en erna staat blijft staan, en aan het eind staat de tekst er weer precies
 * zoals hij was. Schermlezers lezen een verborgen kopie, nooit een tussenstand.
 *
 * @typedef {object} CountUpRefs
 * @property {HTMLElement} display - Het zichtbare getal
 *
 * @extends {Component<CountUpRefs>}
 */
export class CountUpComponent extends Component {
  requiredRefs = ['display'];

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {ParsedNumber | null} */
  #number = null;

  /** @type {string} */
  #text = '';

  /** @type {number | undefined} */
  #frame;

  connectedCallback() {
    super.connectedCallback();

    if (window.Shopify?.designMode || matchMedia(REDUCED_MOTION_QUERY).matches) return;

    this.#text = this.refs.display.textContent ?? '';
    this.#number = parse(this.#text);
    if (!this.#number || this.#number.value === 0) return;

    this.#render(0);
    this.#observer = new IntersectionObserver(this.#onIntersect, { threshold: 0.6 });
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    if (this.#number) this.refs.display.textContent = this.#text;
  }

  /** @param {IntersectionObserverEntry[]} entries */
  #onIntersect = (entries) => {
    if (!entries.some((entry) => entry.isIntersecting)) return;
    this.#observer?.disconnect();
    setTimeout(() => this.#count(), Number(this.dataset.delay) || 0);
  };

  #count() {
    const number = this.#number;
    if (!number) return;

    /** @type {number | undefined} */
    let start;
    /** @param {number} now */
    const tick = (now) => {
      start ??= now;
      const progress = Math.min(1, (now - start) / DURATION_MS);
      // Ease-out: snel omhoog, rustig uitlopen op de echte waarde.
      const eased = 1 - Math.pow(1 - progress, 3);

      if (progress < 1) {
        this.#render(number.value * eased);
        this.#frame = requestAnimationFrame(tick);
      } else {
        this.refs.display.textContent = this.#text;
      }
    };
    this.#frame = requestAnimationFrame(tick);
  }

  /** @param {number} value */
  #render(value) {
    const number = this.#number;
    if (!number) return;
    this.refs.display.textContent = `${number.before}${format(value, number)}${number.after}`;
  }
}

/**
 * @param {string} text
 * @returns {ParsedNumber | null}
 */
function parse(text) {
  const match = text.match(NUMBER);
  if (!match || match.index === undefined) return null;

  const token = match[0];
  const [whole = '', fraction = ''] = token.split(',');
  const separator = whole.match(/[.  ]/)?.[0] ?? '';

  return {
    before: text.slice(0, match.index),
    after: text.slice(match.index + token.length),
    value: Number(whole.replace(/[.  ]/g, '') + (fraction ? `.${fraction}` : '')),
    decimals: fraction.length,
    separator,
  };
}

/**
 * Schrijft een tussenstand net als het origineel: zelfde scheiding, zelfde
 * aantal decimalen. Tijdens het tellen naar beneden afgerond, zodat het
 * getal nooit even boven de eindwaarde uitkomt.
 *
 * @param {number} value
 * @param {ParsedNumber} number
 */
function format(value, { decimals, separator }) {
  const factor = 10 ** decimals;
  const [whole = '0', fraction] = (Math.floor(value * factor) / factor).toFixed(decimals).split('.');
  const grouped = separator ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : whole;
  return fraction ? `${grouped},${fraction}` : grouped;
}

if (!customElements.get('count-up-component')) {
  customElements.define('count-up-component', CountUpComponent);
}
